/**
 * The MSR event day/date model's client (migrations 2026-10-04-200..250, activation work 2026-10-09).
 *
 * Once an event is in the date model (system_config.msr_date_model_enabled, or a per-event opt-in), the database
 * refuses direct writes to its schedule columns (EVENT_SCHEDULE_WRITER_ONLY) and to its Sales Reps / staffing rows
 * (EVENT_STAFFING_WRITER_ONLY). They change only through two writers, which take the event's coordination lock
 * first and keep event_days consistent:
 *   - set_event_dates(event, start_time, end_time, timezone, date_tbd)
 *   - set_event_staffing(event, {assignments, day_closers}, expected_version)
 * For an event NOT in the model nothing changes: callers keep their direct writes. Every helper here asks the
 * server (msr_date_model_active) instead of guessing.
 */
import { supabase } from "./supabase";

type PgError = { code?: string; message?: string; details?: string };

export const SCHEDULE_FIELDS = [
  "start_time",
  "end_time",
  "timezone",
  "date_tbd",
  "start_date",
  "end_date",
] as const;

/** Is this event's schedule/staffing owned by the date-model writers? */
export async function isDateModelActive(eventId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("msr_date_model_active", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return data === true;
}

/** Retry a writer call once or twice when the event is momentarily busy (lock timeout, 55P03). */
async function withBusyRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await fn();
    } catch (e) {
      const code = (e as PgError | null)?.code;
      const msg = (e as PgError | null)?.message ?? "";
      // MSR_TXN_NOT_CLEAN / MSR_LOCK_ORDER / MSR_LOCK_FOREIGN are contract errors, not "busy": never retry them.
      if (
        code !== "55P03" ||
        /^MSR_(TXN_NOT_CLEAN|LOCK_ORDER|LOCK_FOREIGN)/.test(msg)
      ) {
        throw e;
      }
      lastErr = e;
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  throw lastErr;
}

export interface EventSchedule {
  start_time: string | null;
  end_time: string | null;
  timezone: string | null;
  date_tbd: boolean | null;
}

/** The start the forms store for a date-TBD event (EventForm's TBD_PLACEHOLDER_DATE, read by the server as UTC). */
export const TBD_PLACEHOLDER_START = "9999-12-31T00:00:00";

/**
 * An instant for comparing schedule strings. A timestamp without an offset ("9999-12-31T00:00:00", what the form
 * sends for TBD) is read as UTC -- that is how PostgREST's UTC session casts it -- not as browser-local time.
 */
export function scheduleInstant(v: unknown): number | null {
  if (typeof v !== "string" || v.trim() === "") {
    return null;
  }
  const s = v.trim();
  const hasZone = /(?:[zZ]|[+-]\d{2}(?::?\d{2})?)$/.test(s) && /T|\s\d/.test(s);
  const t = new Date(hasZone ? s : `${s.replace(" ", "T")}Z`).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * The complete schedule set_event_dates should receive for a patch, given the event's current schedule.
 * Patch semantics match the legacy direct update: a key that is absent OR undefined (JSON drops it) keeps the
 * stored value; an explicit null clears it. A date-TBD result is always the canonical TBD shape the database holds
 * (start = the given start or the placeholder, end_time null, timezone null): the writer requires every argument,
 * and a kept real end_time would sit before the 9999 placeholder start (events_end_after_start).
 */
export function normalizeScheduleForWriter(
  patch: Record<string, unknown>,
  cur: EventSchedule,
): EventSchedule {
  const given = (k: keyof EventSchedule) => patch[k] !== undefined;
  const take = <T>(k: keyof EventSchedule, fallback: T) =>
    (given(k) ? patch[k] : fallback) as T;
  const date_tbd = given("date_tbd")
    ? patch.date_tbd === true
    : cur.date_tbd === true;
  if (date_tbd) {
    const start = take<string | null>("start_time", cur.start_time);
    return {
      start_time:
        typeof start === "string" && start.trim() !== ""
          ? start
          : TBD_PLACEHOLDER_START,
      end_time: null,
      timezone: null,
      date_tbd: true,
    };
  }
  return {
    start_time: take<string | null>("start_time", cur.start_time) ?? null,
    end_time: take<string | null>("end_time", cur.end_time) ?? null,
    timezone: take<string | null>("timezone", cur.timezone) ?? null,
    date_tbd: false,
  };
}

/** Same schedule? Timestamps compare as instants (any ISO/offset spelling), the rest by value. */
export function sameSchedule(a: EventSchedule, b: EventSchedule): boolean {
  const sameTs = (x: string | null, y: string | null) =>
    ((x ?? null) === null && (y ?? null) === null) ||
    ((x ?? null) !== null &&
      (y ?? null) !== null &&
      (x === y ||
        (scheduleInstant(x) !== null &&
          scheduleInstant(x) === scheduleInstant(y))));
  return (
    sameTs(a.start_time, b.start_time) &&
    sameTs(a.end_time, b.end_time) &&
    (a.timezone ?? null) === (b.timezone ?? null) &&
    (a.date_tbd === true) === (b.date_tbd === true)
  );
}

/**
 * Change an event's dates through set_event_dates (the only schedule writer for a date-model event). Every one of
 * the writer's five parameters is always sent -- null, never undefined, which JSON would drop and PostgREST would
 * then fail to resolve (PGRST202).
 */
export async function setEventDates(
  eventId: string,
  s: EventSchedule,
): Promise<void> {
  await withBusyRetry(async () => {
    const { error } = await supabase.rpc("set_event_dates", {
      p_event_id: eventId,
      p_start_time: s.start_time ?? null,
      p_end_time: s.end_time ?? null,
      p_timezone: s.timezone ?? null,
      p_date_tbd: s.date_tbd === true,
    });
    if (error) {
      throw error;
    }
  });
}

export interface StaffingAssignment {
  position_id: string;
  role: "lead" | "assigned";
  day_scope: "all_days" | "selected";
  days: string[];
}

export interface EventStaffing {
  version: number;
  assignments: StaffingAssignment[];
  day_closers: Record<string, string>;
}

/**
 * The event's current staffing, in set_event_staffing's input shape, with its optimistic version.
 *
 * ONE server read (get_event_staffing, migration 409): the version and the rows it describes come from a single
 * database snapshot. Four independent PostgREST reads could pair rows read BEFORE another user's save with the
 * version read AFTER it, and the full-set replacement would then silently drop that user's rep (steps 1-4 audit,
 * P1). With one snapshot a stale read carries its stale version, so the writer refuses it
 * (STAFFING_VERSION_CONFLICT) and changeEventStaffing re-reads.
 */
export async function getEventStaffing(
  eventId: string,
): Promise<EventStaffing> {
  const { data, error } = await supabase.rpc("get_event_staffing", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  const raw = (data ?? {}) as {
    version?: number | null;
    assignments?: {
      position_id: string;
      role: string;
      day_scope: string | null;
      days?: string[] | null;
    }[];
    day_closers?: Record<string, string> | null;
  };
  if (typeof raw.version !== "number") {
    throw new Error("get_event_staffing returned no version");
  }
  const assignments = (raw.assignments ?? []).map((a) => {
    const scope = a.day_scope === "selected" ? "selected" : "all_days";
    return {
      position_id: a.position_id,
      role: a.role === "lead" ? "lead" : "assigned",
      day_scope: scope,
      days: scope === "selected" ? [...(a.days ?? [])] : [],
    } as StaffingAssignment;
  });
  return {
    version: raw.version,
    assignments,
    day_closers: { ...(raw.day_closers ?? {}) },
  };
}

/**
 * Apply one change to an event's staffing through set_event_staffing. Reads the current staffing and version,
 * applies `change`, and writes the whole set; if someone else changed it in between (STAFFING_VERSION_CONFLICT),
 * re-reads and re-applies once.
 */
export async function changeEventStaffing(
  eventId: string,
  change: (current: StaffingAssignment[]) => StaffingAssignment[],
): Promise<void> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const cur = await getEventStaffing(eventId);
    const next = change(
      cur.assignments.map((a) => ({ ...a, days: [...a.days] })),
    );
    const positions = new Set(next.map((a) => a.position_id));
    const day_closers = Object.fromEntries(
      Object.entries(cur.day_closers).filter(([, pos]) => positions.has(pos)),
    );
    try {
      await withBusyRetry(async () => {
        const { error } = await supabase.rpc("set_event_staffing", {
          p_event_id: eventId,
          p_staffing: { assignments: next, day_closers },
          p_expected_version: cur.version,
        });
        if (error) {
          throw error;
        }
      });
      return;
    } catch (e) {
      if (
        attempt === 0 &&
        /STAFFING_VERSION_CONFLICT/.test((e as PgError | null)?.message ?? "")
      ) {
        continue;
      }
      throw e;
    }
  }
}

/**
 * GS-05 Confirm schedule: write the whole staffing set the grid planned, against the version the grid was loaded with.
 * No silent re-apply on a version conflict (unlike changeEventStaffing): the operator was looking at the old schedule,
 * so STAFFING_VERSION_CONFLICT is shown ("someone else changed ... reload") and the grid reloads.
 */
export async function setEventStaffingExact(
  eventId: string,
  staffing: Pick<EventStaffing, "assignments" | "day_closers">,
  expectedVersion: number,
): Promise<void> {
  await withBusyRetry(async () => {
    const { error } = await supabase.rpc("set_event_staffing", {
      p_event_id: eventId,
      p_staffing: staffing,
      p_expected_version: expectedVersion,
    });
    if (error) {
      throw error;
    }
  });
}

/**
 * The staffing after making `lead` the event's Lead rep (MSR activation fix 6: the form's Lead/Event Rep and the
 * Sales Reps list must not drift). The lead works every day. Anyone else who was a lead stays on the list as a
 * rep ('assigned', same days): the writer allows one lead, and taking someone off the list is a separate,
 * deliberate action (owner rule: no automatic removal). Returns null when nothing changes.
 */
export function planLeadRep(
  cur: StaffingAssignment[],
  lead: string,
): { next: StaffingAssignment[]; demoted: string[] } | null {
  const mine = cur.find((a) => a.position_id === lead);
  const otherLeads = cur.filter(
    (a) => a.position_id !== lead && a.role === "lead",
  );
  if (
    mine &&
    mine.role === "lead" &&
    mine.day_scope === "all_days" &&
    otherLeads.length === 0
  ) {
    return null;
  }
  const next: StaffingAssignment[] = [
    { position_id: lead, role: "lead", day_scope: "all_days", days: [] },
    ...cur
      .filter((a) => a.position_id !== lead)
      .map((a) =>
        a.role === "lead"
          ? { ...a, role: "assigned" as const, days: [...a.days] }
          : { ...a, days: [...a.days] },
      ),
  ];
  return { next, demoted: otherLeads.map((a) => a.position_id) };
}

/** Turn a date-model writer refusal into a sentence an operator can act on; null when it is not one of them. */
/** The wording of the server's LEAD_REP_NOT_PERMITTED refusal (set_event_lead_rep, migration 417). */
export const LEAD_REP_NOT_PERMITTED_MESSAGE =
  "Only full-time staff can change an event's Sales Reps list.";

export function describeDateModelError(e: unknown): string | null {
  const msg = `${(e as PgError | null)?.message ?? ""} ${(e as PgError | null)?.details ?? ""}`;
  if (/STAFFING_ALL_DAYS_NEEDS_ALL_DAYS_LEAD/.test(msg)) {
    return "Add a Lead rep for all days first; other reps on every day need a lead who is also on every day.";
  }
  if (/STAFFING_DAY_WITHOUT_CLOSER/.test(msg)) {
    return "Every day with a rep needs someone to close it: put the Lead on that day or name a closer.";
  }
  if (/STAFFING_SELECTED_WITHOUT_DAYS/.test(msg)) {
    return "Choose at least one day for this rep, or put them on all days.";
  }
  if (/ACTIVE_DAY_REMOVAL/.test(msg)) {
    // Migration 415: parallel to the staffed-day refusal below.
    return "That date change would remove a day that already has check-ins or rewards. Keep that day in the event's dates.";
  }
  if (/STAFFED_DAY_REMOVAL/.test(msg)) {
    return "That date change would remove a day that has reps on it. Move or remove those reps first.";
  }
  if (/MSR_DAY_HAS_RECORD/.test(msg)) {
    // Slice 4 (migration 503): a started or closed day is a permanent record.
    return "That date change would remove a day that was already started or closed. Keep that day in the event's dates.";
  }
  if (/MSR_DAY_HAS_LATE_FLAG/.test(msg)) {
    // Reachable only if the owner flips msr_slice4_policy('late_flag_only_day_change') to 'refuse' (migration 503).
    // With the shipped decision ('move') a late-flag-only day moves and its flag removal is audited, so this is
    // currently unreachable; kept so the switch can be flipped without a frontend release (final audit P3-7).
    return "That date change would remove a day flagged as not closed. Have a manager complete its close, or keep that day in the event's dates.";
  }
  if (/STAFFED_EVENT_DATE_TBD/.test(msg)) {
    return "Remove the event's reps before marking its date TBD.";
  }
  if (/set_event_dates: end_time must be after start_time/.test(msg)) {
    return "The event's end must be after its start. Check the end date and end time.";
  }
  {
    // Migration 504 (owner decision 2026-10-12): a rep who started a day that is not closed cannot be taken off the
    // Sales Reps list, have that day removed from their days, or lose their sales position (deactivated, moved to
    // someone else, deleted). The server names the rep, the day and the event; show its sentence without the code.
    const m = /MSR_REP_HAS_OPEN_DAY:\s*(.*)$/s.exec(
      (e as PgError | null)?.message ?? "",
    );
    if (m) {
      return m[1].trim();
    }
  }
  if (/MSR_REP_REMOVAL_BUSY/.test(msg)) {
    return "A rep on this position's events is starting or closing a day right now. Try again in a moment.";
  }
  {
    // Migration 416: the server names the events; show its sentence without the code.
    const m = /SALES_POSITION_STAFFED:\s*(.*)$/s.exec(
      (e as PgError | null)?.message ?? "",
    );
    if (m) {
      // GS-05 renamed the tab; the server sentence (mig 416) still says "(Sales Reps tab)"
      return m[1]
        .trim()
        .replace(/\(Sales Reps tab\)/g, "(Confirm Schedule tab)");
    }
  }
  if (/LEAD_REP_POSITION_INACTIVE/.test(msg)) {
    return "That rep's sales position is inactive. Choose an active rep.";
  }
  if (/LEAD_REP_NEEDS_EVENT_EDIT/.test(msg)) {
    // Owner ruling (migration 417, P2-4): changing who is Lead needs edit rights on the event itself.
    return "Changing the event's Lead rep needs edit rights on this event. Ask the event's owner, a manager or an admin. You can still add or remove other reps.";
  }
  if (/LEAD_REP_CHANGED/.test(msg)) {
    return "The event's rep was changed by someone else. Reload the event to see the current rep, then try again.";
  }
  if (/LEAD_REP_NOT_PERMITTED/.test(msg)) {
    return LEAD_REP_NOT_PERMITTED_MESSAGE;
  }
  if (/EVENT_STAFFING_WRITER_ONLY/.test(msg)) {
    return "This rep is on the Sales Reps list of an event. Remove them from that event's Confirm Schedule tab first.";
  }
  if (/STAFFING_VERSION_CONFLICT/.test(msg)) {
    return "Someone else changed this event's reps at the same time. Reload and try again.";
  }
  if ((e as PgError | null)?.code === "55P03") {
    return "This event is busy (another change is being saved). Try again in a moment.";
  }
  return null;
}

export interface StaffingCopyFailure {
  eventId: string;
  message: string;
}

/**
 * Copy the source event's Sales Reps onto freshly duplicated events (MSR plan: "the copy gets the source's
 * assignment list"). Only for duplicates that are in the date model -- before activation nothing changes -- and
 * only all-days reps: a 'selected' rep's days belong to the source's dates and cannot be mapped. Each copy is its
 * own set_event_staffing call (duplicate_event already holds locks, so the writer cannot run inside it).
 *
 * Best effort and NEVER throws: the duplicates already exist (duplicate_event committed), so every problem is
 * returned per event for the caller to show, instead of turning a successful duplication into a reported failure.
 * The source's staffing is read only when at least one duplicate is in the model, so a legacy duplication makes no
 * staffing read at all.
 */
export async function copyStaffingToDuplicates(
  sourceEventId: string,
  newEventIds: string[],
): Promise<{
  copied: string[];
  failed: StaffingCopyFailure[];
}> {
  const out = {
    copied: [] as string[],
    failed: [] as StaffingCopyFailure[],
  };
  const messageOf = (e: unknown, fallback: string) =>
    describeDateModelError(e) ??
    (e as { message?: string } | null)?.message ??
    fallback;
  const inModel: string[] = [];
  for (const id of newEventIds) {
    try {
      if (await isDateModelActive(id)) {
        inModel.push(id);
      }
    } catch (e) {
      out.failed.push({
        eventId: id,
        message: `could not check whether its Sales Reps need copying (${messageOf(e, "check failed")})`,
      });
    }
  }
  if (inModel.length > 0) {
    let toCopy: StaffingAssignment[] | null = null;
    try {
      const source = await getEventStaffing(sourceEventId);
      toCopy = source.assignments
        .filter((a) => a.day_scope === "all_days")
        .map((a) => ({ ...a, days: [] }));
    } catch (e) {
      const why = `the original event's Sales Reps could not be read (${messageOf(e, "read failed")})`;
      for (const id of inModel) {
        out.failed.push({ eventId: id, message: why });
      }
    }
    if (toCopy && toCopy.length > 0) {
      const reps = toCopy;
      for (const id of inModel) {
        try {
          await changeEventStaffing(id, () => reps);
          out.copied.push(id);
        } catch (e) {
          out.failed.push({
            eventId: id,
            message: messageOf(e, "copy failed"),
          });
        }
      }
    }
  }
  if (out.failed.length > 0) {
    console.error(
      "[duplicate] Sales Reps could not be copied to some duplicates:",
      out.failed,
    );
  }
  return out;
}
